import { NO, UIColor, UITextView, UIView, YES } from "uicore-ts"
import { RHButton } from "./RHButton"
import { RHColor } from "./RHColor"


/**
 * A collapsible accordion panel with a clickable header and animated content area.
 *
 * The content slides out from below the header: the container clips overflow while
 * the inner content view translates from -100% (fully hidden above the clip) to 0
 * (fully visible). The container height animates simultaneously so surrounding
 * layout reflows smoothly.
 *
 * Set `accordion.animated = false` to skip transitions and snap instantly.
 *
 * Usage:
 *   const accordion = new RHAccordionView()
 *   accordion.title = "Hanke üldandmed"
 *   accordion.contentView = myDetailsView
 *   accordion.expanded = true
 */
export class RHAccordionView extends UIView {
    
    static readonly animationDuration = 0.28 // seconds — UIView API uses seconds
    
    readonly headerButton = new RHButton()
        .configuredWithObject({ variant: "light" })
        .addedAsSubviewToView(this)
    
    readonly chevronLabel = new UITextView()
        .configuredWithObject({
            isSingleLine: YES,
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "14px",
                color: RHColor.textMuted.stringValue,
                userSelect: "none",
                pointerEvents: "none"
            }
        })
        .addedAsSubviewToView(this)
    
    // The clipping container — overflow hidden clips the sliding content
    readonly contentContainer = new UIView()
        .configuredWithObject({
            style: { overflow: "hidden" }
        })
        .addedAsSubviewToView(this)
    
    _expanded = NO
    _contentView?: UIView
    _title = ""
    // Cached intrinsic height of the content so we can animate to the right value
    _contentHeight = 0
    // When false, toggling expanded snaps immediately without animation
    animated = NO
    
    
    constructor(elementID?: string) {
        super(elementID);
        (this as RHAccordionView).configureWithObject({
            style: {
                border: `1px solid ${RHColor.borderLight.stringValue}`,
                borderRadius: "8px",
                overflow: "hidden"
            }
        })
        
        this.headerButton.configureWithObject({
            style: {
                borderRadius: "0px",
                textAlign: "left"
            }
        })
        
        this._updateChevron()
        
        // noinspection JSConstantReassignment
        this.headerButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.expanded = !this._expanded
        }
    }
    
    // ── Public API ────────────────────────────────────────────────────────────
    
    get title(): string {
        return this._title
    }
    
    set title(title: string) {
        this._title = title
        this.headerButton.titleLabel.text = title
    }
    
    get expanded(): boolean {
        return this._expanded
    }
    
    set expanded(expanded: boolean) {
        if (this._expanded === expanded) { return }
        this._expanded = expanded
        this._updateChevron()
        this._performContentTransition()
        // Tell the parent hierarchy that our intrinsic height changed
        this.setNeedsLayoutUpToRootView()
    }
    
    get contentView(): UIView | undefined {
        return this._contentView
    }
    
    set contentView(view: UIView | undefined) {
        this._contentView?.removeFromSuperview()
        this._contentView = view
        if (view) {
            view.addedAsSubviewToView(this.contentContainer)
        }
        this.setNeedsLayout()
    }
    
    // ── Private helpers ───────────────────────────────────────────────────────
    
    _updateChevron() {
        this.chevronLabel.text = this._expanded ? "▲" : "▼"
    }
    
    /**
     * Animates both the container height and the inner content translateY together:
     *  • Opening:  height 0 → _contentHeight,  translateY -100% → 0%
     *  • Closing:  height _contentHeight → 0,  translateY 0% → -100%
     *
     * The "from" state is set instantly before the animation block so the
     * framework sees a real before/after delta to transition between.
     * When `animated` is false the transition is skipped entirely.
     */
    _performContentTransition() {
        if (!this._contentView) { return }
        
        const targetTranslate = this._expanded ? "translateY(0%)" : "translateY(-100%)"
        const targetHeight = `${this._expanded ? this._contentHeight : 0}px`
        
        // Set the "from" state instantly (no animation) so the framework has a
        // starting point to interpolate from
        if (this._expanded) {
            this._contentView.style.transform = "translateY(-100%)"
            this.contentContainer.style.height = "0px"
        }
        else {
            this._contentView.style.transform = "translateY(0%)"
            this.contentContainer.style.height = `${this._contentHeight}px`
        }
        
        if (!this.animated) {
            // Snap directly to target — no transition
            this._contentView.style.transform = targetTranslate
            this.contentContainer.style.height = targetHeight
            return
        }
        
        UIView.animateViewOrViewsWithDurationDelayAndFunction(
            [this._contentView, this.contentContainer],
            RHAccordionView.animationDuration,
            0,
            "ease",
            () => {
                this._contentView!.style.transform = targetTranslate
                this.contentContainer.style.height = targetHeight
                
            },
            () => { /* animation complete */ }
        )
    }
    
    // ── Layout ────────────────────────────────────────────────────────────────
    
    override layoutSubviews() {
        super.layoutSubviews()
        
        const padding = this.core.paddingLength
        const bounds = this.contentBoundsWithInset(0)
        const headerHeight = padding * 3
        
        // Header row: button fills full width, chevron overlaid on the right
        this.headerButton.frame = bounds.rectangleWithHeight(headerHeight)
        this.chevronLabel.frame = bounds
            .rectangleWithHeight(headerHeight)
            .rectangleWithWidth(padding * 2, 1)
        
        if (!this._contentView) {
            this.contentContainer.frame = this.headerButton.frame.rectangleWithHeight(0)
            return
        }
        
        // Always measure the content so _contentHeight stays current
        this._contentHeight = this._contentView.intrinsicContentHeight(bounds.width)
        
        // Position the content view at the top of the container.
        // translateY slides it in/out — layout always places it at Y=0.
        this._contentView.frame = this.contentContainer.contentBounds
            .rectangleWithHeight(this._contentHeight)
        
        // During a layout pass set state directly, no transition, so the
        // framework's frame system doesn't fight any ongoing animation
        this._contentView.style.transform = this._expanded ? "translateY(0%)" : "translateY(-100%)"
        this.contentContainer.style.height = this._expanded ? `${this._contentHeight}px` : "0px"
        
        // Frame drives x/y/width; height is governed by the style property above
        this.contentContainer.frame = this.headerButton.frame
            .rectangleForNextRow(0, this._expanded ? this._contentHeight : 0)
    }
    
}
